import React, {useEffect, useState, useRef, useCallback, useMemo} from 'react';
import {fetchJson} from '../../helpers/index.js';
import Button from '../bootstrap/Button.jsx';
import {Adalicon} from '../common/Icons.jsx';
import {closeModal, Modal} from '../common/Modal.jsx';
import ToggleSwitch from '../common/ToggleSwitch.jsx'
import Select from '../common/Select.jsx';
import Errors from '../common/Errors.jsx';
import AgGrid from '../common/AgGrid/index.jsx';
import {ModuleRegistry} from 'ag-grid-community';
import {TreeDataModule, ValidationModule} from 'ag-grid-enterprise';
import { gridTheme } from '../common/AgGrid/gridTheme.js';

ModuleRegistry.registerModules([
  TreeDataModule,
  ValidationModule,
]);

import {
  // organization_units_list_path,
  organization_units_create_path,
  organization_units_update_path,
  organization_units_delete_path,
  organization_units_bulk_update_path,
  organization_units_move_user_path,
} from '~/routes';

const MODAL_ID = 'organization-unit-modal';

const OrganizationUnitModal = ({handleCloseModal, selectedNode, gridRefApi}) => {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const formRef = useRef();

  const handleClose = () => {
    closeModal(MODAL_ID);
    handleCloseModal()
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    if (selectedNode.isEditing) {
      const url = selectedNode.is_user ?
        organization_units_move_user_path({id: selectedNode.id}) :
        organization_units_update_path({id: selectedNode.id})

      jQuery.post(url, jQuery(formRef.current).serialize())
        .done((res) => {
          gridRefApi.applyTransaction({update: [{...res, name: res.name || selectedNode.name}]});
          handleClose()
        })
        .fail((res) => {
          let responseJSON = res.responseJSON ? res.responseJSON.errors : {server: [{message: I18n.t('unexpected_error')}]};
          setErrors(responseJSON);
          setSaving(false)
        })

    } else {
      jQuery.post(
        organization_units_create_path({}), jQuery(formRef.current).serialize()
      ).done((res) => {
        // Find the clicked node
        const clickedNode = gridRefApi.getRowNode(String(selectedNode.id));
        if (!clickedNode) {
          gridRefApi.applyTransaction({add: [res]}); // add at root
          return;
        }

        gridRefApi.applyTransaction({add: [res], undefined});

        // Expand parent to show new child
        clickedNode.setExpanded(true);

        handleClose()
      }).fail((res) => {
        let responseJSON = res.responseJSON ? res.responseJSON.errors : {server: [{message: I18n.t('unexpected_error')}]};
        setErrors(responseJSON);
        setSaving(false)
      })
    }
  };

  const leafOptions = useMemo(() => {
    if (!gridRefApi) return [];

    const options = [];
    gridRefApi.forEachNode(({data}) => {
      if (data.is_leaf && !data.is_user && data.code) {
        options.push({value: data.code, label: data.name})
      }
    })
    return options;
  }, [gridRefApi, selectedNode]);

  return (
    <Modal
      id={MODAL_ID}
      size='md'
      title={selectedNode.isEditing ? I18n.t('org_unit_modal_edit_title') : I18n.t('org_unit_modal_add_title')}
      saveLabel={selectedNode.isEditing ? I18n.t('update') : I18n.t('add')}
      cancelLabel={I18n.t('close')}
      onClose={handleClose}
      onSave={handleSave}
      saving={saving}
    >
      <Errors errors={errors}/>

      <form ref={formRef} onSubmit={handleSave}>
        {!selectedNode.is_user ?
          <>
            <div className='form-group'>
              <label>{I18n.t('name')} *</label>
              <input type='text' name='organization_unit[name]' className='form-control' defaultValue={selectedNode.name}/>
            </div>

            <div className='form-group'>
              <label>{I18n.t('code')}/{I18n.t('identifier')}</label>
              <input type='text' name='organization_unit[code]' className='form-control' defaultValue={selectedNode.code}/>
            </div>

            <div className='form-group'>
              <label>{I18n.t('type')}</label>
              <input type='text' name='organization_unit[type]' className='form-control' defaultValue={selectedNode.type}/>
            </div>
          </>
          :
          <div className='form-group'>
            <label>{I18n.t('choose_leaf_to_mode_user')}</label>
            <Select
              name='organization_unit[code]'
              options={leafOptions}
              defaultValue={selectedNode.code}
              isClearable
            />
          </div>
        }

        {!selectedNode.isEditing &&
          <div className='form-group'>
            <label>{I18n.t('org_unit_is_leaf_label')}</label>
            <div>
              <ToggleSwitch
                isOn={selectedNode.is_leaf}
                name="organization_unit[is_leaf]"
                checkedValue={true}
                uncheckedValue={false}
                includeFalse
              />
            </div>

            <input type='hidden' name='organization_unit[parent_id]' value={selectedNode.id}/>
          </div>
        }
      </form>
    </Modal>
  );
};

export default function ManageOrganizationHierarchyGrid({
  rowData = [],
  useAsPicker = {enabled: false, url: '', onSelect: () => {}},
  permissions = {}
}) {
  const gridRef = useRef(null);
  const [userRows, setRowData] = useState(rowData);
  const [editMode, setEditMode] = useState(false);
  const [selectedNode, setSelectedNode] = useState({});
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (useAsPicker.enabled) {
      fetchJson(useAsPicker.url).then(res => {
        setRowData(res);
      })
    }
  }, []);

  const handleCloseModal = () => setOpenModal(false);

  const handleAddUnitClick = (parent_id) => {
    setSelectedNode({id: parent_id, isEditing: false});
    setOpenModal(true);
  };

  const handleEditUnitClick = (nodeData) => {
    setSelectedNode({...nodeData, isEditing: true});
    setOpenModal(true);
  };

  const handleDeleteUnitClick = async (id) => {
    if (!confirm(I18n.t('org_unit_delete_confirm'))) {
      return false;
    }

    const rowNode = gridRef.current.api.getRowNode(String(id));
    if (!rowNode) return;

    jQuery.post(organization_units_delete_path({id}))
      .done((_res) => {
        const parentNode = rowNode.parent;

        const rowsToRemove = [];

        // Recursive function to collect parent and all children
        const collectChildren = (node) => {
          rowsToRemove.push(node.data);

          if (node.childrenAfterGroup && node.childrenAfterGroup.length > 0) {
            node.childrenAfterGroup.forEach(child => collectChildren(child));
          }
        }
        collectChildren(rowNode);
        gridRef.current.api.applyTransaction({remove: rowsToRemove});
      })
  };

  const columnDefs = [
    {
      field: 'code',
      headerName: `${I18n.t('code')}/${I18n.t('identifier')}`,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      editable: editMode,
    },
    {
      field: 'type',
      headerName: I18n.t('type'),
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      editable: editMode,
    },
    {
      headerName: I18n.t('actions'),
      pinned: 'right',
      editable: false,
      filter: false,
      sortable: false,
      width: 130,
      hide: !editMode,
      cellRenderer: ({data = {}}) => {
        const isRoot = data.parent_id === null;
        const isLeaf = data.is_leaf;
        const isUser = data.is_user;

        return (
          <div>
            {(!isRoot || isUser) &&
              <Button
                size='xs'
                type='primary'
                onClick={() => handleEditUnitClick(data)}
                style={{marginRight: '0.5rem'}}
              >
                <Adalicon type='edit'/>
              </Button>
            }
            {(!isRoot && !isUser) &&
              <Button
                size='xs'
                type='danger'
                onClick={() => handleDeleteUnitClick(data.id)}
                style={{marginRight: '0.5rem'}}
              >
                <Adalicon type='trash'/>
              </Button>
            }
            {!isLeaf &&
              <Button
                size='xs'
                type='default'
                onClick={() => handleAddUnitClick(data.id)}
                style={{marginRight: '0.5rem'}}
              >
                <Adalicon type='add'/>
              </Button>
            }
          </div>
        )
      }
    },
    {
      pinned: 'right',
      editable: false,
      filter: false,
      sortable: false,
      width: 120,
      hide: !useAsPicker.enabled,
      cellRenderer: ({data = {}}) => {
        if (!data.is_user) return;
        return (
          <Button size='sm' type='primary' onMouseUp={() => useAsPicker.onSelect({label: data.name, value: data.id.split('-')[0]})}>
            {I18n.t('select')} <i className='fa fa-chevron-right'/>
          </Button>
        )
      }
    }
  ];

  const onCellValueChanged = useCallback(async (params) => {
    jQuery.post(organization_units_update_path({id: params.data.id}), {organization_unit: params.data})
      .fail((err) => console.error(err))
  }, []);

  const onRowDragEnd = async (event) => {
    const draggedNodes = event.nodes;
    if (!draggedNodes || draggedNodes.length === 0) return;

    const firstNode = draggedNodes[0];
    const newParentNode = firstNode.parent;
    const newParentId = newParentNode?.data?.id || null;
    const updates = [];

    draggedNodes.forEach(({data}) => {
      if (data.parent_id !== newParentId) {
        data.parent_id = newParentId;
        data.code = newParentNode?.data?.code;
        updates.push(data);
      }
    });

    if (updates.length === 0) return;

    jQuery.post(organization_units_bulk_update_path({}), {organization_unit: {parent_id: newParentId, ids: updates.map(v => v.id), dragged: true}})
      .done((_res) => {
        gridRef.current.api.applyTransaction({update: updates});
      })
      .fail((_err) => {
        gridRef.current.api.refreshClientSideRowModel('treeDataChanged');
      });
  };

  const isRowValidDropPosition = useCallback((params) => {
    const s_data = params.source.data;
    const t_data = params.newParent?.data || params.target?.data;

    if (!t_data || !s_data) return true;

    const isInvalid =
      t_data.is_user ||                           // Target is a user
      (s_data.is_user && !t_data.is_leaf) ||      // User moved to a non-leaf
      (!s_data.is_leaf && t_data.is_leaf) ||      // Folder moved to a leaf
      (t_data.is_leaf && !t_data.is_user && !s_data.is_user) || // Leaf-to-leaf
      (s_data.is_user && t_data.is_leaf && !t_data.code); // User moved to a leaf without a Code/Identifier

    return !isInvalid;
  }, []);

  const getContextMenuItems = ({node}) => {
    if (!editMode || !node || node.data.is_user) return [];

    const isRoot = node.data.parent_id === null;
    const isLeaf = node.data.is_leaf;
    const menu = [];

    if (!isLeaf) {
      menu.push({
        name: I18n.t('org_unit_add_sub_unit'),
        action: () => handleAddUnitClick(node.data.id),
      })
    }

    if (!isRoot) {
      menu.push({
        name: I18n.t('edit'),
        action: () => handleEditUnitClick(node.data),
      })

      menu.push({
        name: I18n.t('delete'),
        action: () => handleDeleteUnitClick(node.data.id),
      })
    }
    return menu;
  };

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: I18n.t('name'),
      field: 'name',
      rowDrag: ({data = {}}) => editMode && (data.parent_id !== null || data.is_user),
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: ({node, data}) => {
          if (!data) return null;

          const childCount = node?.childrenAfterGroup?.length || 0;

          const iconClass = data.is_user ? 'fa fa-user text-muted' : data.is_leaf ? 'fa fa-users text-primary' : 'fa-solid fa-building text-info';
          return (
            <div>
              <i className={iconClass} style={{marginRight: 6}}/>
              {data.name}
              {childCount > 0 && <span style={{marginLeft: 6, color: "#999", fontSize: 12}}>({childCount})</span>}
            </div>
          );
        },
      },
    };
  }, [editMode]);

  return (
    <>
      {permissions.may_manage_org_hierarchy &&
        <div style={{marginBottom: 10, textAlign: 'right'}}>
          <button onClick={() => setEditMode(!editMode)} className={`btn btn-${editMode ? 'default' : 'primary'}`}>
            <i className={`fa fa-${editMode ? 'eye' : 'cog'}`}/> {editMode ? I18n.t('switch_to_view_mode') : I18n.t('switch_to_edit_mode')}
          </button>
        </div>
      }

      <div className='grid-theme' style={{height: 'calc(100vh - 250px)'}}>
        <AgGrid
          theme={gridTheme}
          ref={gridRef}
          rowData={userRows}
          columnDefs={columnDefs}
          treeData={true}
          treeDataParentIdField={'parent_id'}
          animateRows={true}
          suppressMoveWhenRowDragging={true}
          rowDragManaged={editMode}
          rowDragEntireRow={editMode}
          rowDragMultiRow={true}
          onRowDragEnd={onRowDragEnd}
          isRowValidDropPosition={isRowValidDropPosition}
          getRowId={(params) => String(params.data.id)}
          autoGroupColumnDef={autoGroupColumnDef}
          rowSelection={editMode ? {mode: 'multiRow'} : {}}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            suppressHeaderMenuButton: true,
          }}
          onCellValueChanged={onCellValueChanged}
          getContextMenuItems={getContextMenuItems}
          groupDefaultExpanded={-1}
        />
      </div>

      {openModal && <OrganizationUnitModal {...{handleCloseModal, selectedNode, gridRefApi: gridRef.current.api}} />}
    </>
  );
}
